import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        await checkSubscription();
      }
      setLoading(false);
    };
    checkSupport();
  }, []);

  const checkSubscription = async () => {
    try {
      const { data } = await axios.get(`${API}/push/status`);
      setIsSubscribed(data.subscribed);
    } catch (e) {
      console.error('Error checking push status:', e);
    }
  };

  const subscribe = useCallback(async () => {
    if (!isSupported) return { success: false, error: 'Push not supported' };

    try {
      // Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        return { success: false, error: 'Permission denied' };
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const { data: vapidData } = await axios.get(`${API}/push/vapid-key`);
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey)
      });

      const subJson = subscription.toJSON();
      
      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      });

      setIsSubscribed(true);
      
      // Schedule daily 10 AM reminder
      scheduleLocalReminder();
      
      return { success: true };
    } catch (e) {
      console.error('Push subscription error:', e);
      return { success: false, error: e.message };
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }
      
      await axios.delete(`${API}/push/unsubscribe`);
      setIsSubscribed(false);
      return { success: true };
    } catch (e) {
      console.error('Unsubscribe error:', e);
      return { success: false, error: e.message };
    }
  }, []);

  const scheduleLocalReminder = () => {
    // Schedule a local reminder for 10 AM
    const now = new Date();
    const target = new Date();
    target.setHours(10, 0, 0, 0);
    
    // If it's past 10 AM, schedule for tomorrow
    if (now >= target) {
      target.setDate(target.getDate() + 1);
    }
    
    const delay = target.getTime() - now.getTime();
    
    // Send message to service worker to schedule notification
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SCHEDULE_NOTIFICATION',
        delay,
        title: 'Bible Buds',
        body: "Good morning! Time for today's Bible lesson!"
      });
    }
  };

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    checkSubscription
  };
}
